import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, BarChart3, PieChart, Activity, DollarSign } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart as RechartsPieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend } from 'recharts';
import { useBills } from '@/hooks/useBills';
import { useDashboard } from '@/hooks/useDashboard';

interface AnalyticsData {
  month: string;
  processed: number;
  amount: number;
  confidence: number;
  errors: number;
}

interface SupplierAnalytics {
  name: string;
  count: number;
  amount: number;
  avgConfidence: number;
  color: string;
}

export const AdvancedAnalytics = () => {
  const { bills } = useBills();
  const { stats } = useDashboard();
  const [timeRange, setTimeRange] = useState('6months');
  const [metric, setMetric] = useState('processed');

  // Process data for analytics
  const processAnalyticsData = (): AnalyticsData[] => {
    const months = 6; // Default 6 months
    const data: AnalyticsData[] = [];
    const now = new Date();

    for (let i = months - 1; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      
      const monthBills = bills?.filter(bill => {
        const billDate = new Date(bill.created_at);
        return billDate.getMonth() === date.getMonth() && 
               billDate.getFullYear() === date.getFullYear();
      }) || [];

      data.push({
        month: monthKey,
        processed: monthBills.filter(b => b.processing_status === 'completed').length,
        amount: monthBills.reduce((sum, b) => sum + Number(b.amount), 0),
        confidence: monthBills.reduce((sum, b) => sum + (b.ai_confidence || 0), 0) / Math.max(monthBills.length, 1),
        errors: monthBills.filter(b => b.processing_status === 'error').length,
      });
    }

    return data;
  };

  const processSupplierData = (): SupplierAnalytics[] => {
    const supplierMap = new Map<string, { count: number; amount: number; confidenceSum: number; }>();
    const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#8dd1e1', '#d084d0'];

    bills?.forEach(bill => {
      if (bill.supplier_name && bill.processing_status === 'completed') {
        const existing = supplierMap.get(bill.supplier_name) || { count: 0, amount: 0, confidenceSum: 0 };
        supplierMap.set(bill.supplier_name, {
          count: existing.count + 1,
          amount: existing.amount + Number(bill.amount),
          confidenceSum: existing.confidenceSum + (bill.ai_confidence || 0),
        });
      }
    });

    return Array.from(supplierMap.entries()).map(([name, data], index) => ({
      name,
      count: data.count,
      amount: data.amount,
      avgConfidence: data.confidenceSum / data.count,
      color: colors[index % colors.length],
    }));
  };

  const analyticsData = processAnalyticsData();
  const supplierData = processSupplierData();
  const processingAccuracy = stats?.kpis.processingRate || 0;
  const totalRevenue = stats?.kpis.totalRevenue || 0;
  const totalBills = stats?.kpis.totalBills || 0;

  // Calculate trends
  const currentMonth = analyticsData[analyticsData.length - 1];
  const previousMonth = analyticsData[analyticsData.length - 2];
  const billsTrend = currentMonth && previousMonth ? 
    ((currentMonth.processed - previousMonth.processed) / Math.max(previousMonth.processed, 1)) * 100 : 0;
  const revenueTrend = currentMonth && previousMonth ? 
    ((currentMonth.amount - previousMonth.amount) / Math.max(previousMonth.amount, 1)) * 100 : 0;

  const chartConfig = {
    processed: {
      label: "Processed Bills",
      color: "hsl(var(--chart-1))",
    },
    amount: {
      label: "Amount ($)",
      color: "hsl(var(--chart-2))",
    },
    confidence: {
      label: "Confidence %",
      color: "hsl(var(--chart-3))",
    },
    errors: {
      label: "Errors",
      color: "hsl(var(--chart-4))",
    },
  };

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Advanced Analytics</h2>
          <p className="text-muted-foreground">
            Comprehensive insights into your bill processing performance
          </p>
        </div>
        <div className="flex space-x-4">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3months">3 Months</SelectItem>
              <SelectItem value="6months">6 Months</SelectItem>
              <SelectItem value="1year">1 Year</SelectItem>
            </SelectContent>
          </Select>
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="processed">Bills Processed</SelectItem>
              <SelectItem value="amount">Revenue</SelectItem>
              <SelectItem value="confidence">AI Confidence</SelectItem>
              <SelectItem value="errors">Processing Errors</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Processing Accuracy</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(processingAccuracy || 0).toFixed(1)}%</div>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              {billsTrend >= 0 ? (
                <TrendingUp className="h-3 w-3 text-green-500" />
              ) : (
                <TrendingDown className="h-3 w-3 text-red-500" />
              )}
              <span className={billsTrend >= 0 ? 'text-green-500' : 'text-red-500'}>
                {Math.abs(billsTrend || 0).toFixed(1)}% from last month
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${totalRevenue.toLocaleString()}</div>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground">
              {revenueTrend >= 0 ? (
                <TrendingUp className="h-3 w-3 text-green-500" />
              ) : (
                <TrendingDown className="h-3 w-3 text-red-500" />
              )}
              <span className={revenueTrend >= 0 ? 'text-green-500' : 'text-red-500'}>
                {Math.abs(revenueTrend || 0).toFixed(1)}% from last month
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Confidence</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {currentMonth ? (currentMonth.confidence || 0).toFixed(1) : '0'}%
            </div>
            <p className="text-xs text-muted-foreground">
              AI extraction confidence
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Suppliers</CardTitle>
            <PieChart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{supplierData.length}</div>
            <p className="text-xs text-muted-foreground">
              Unique utility providers
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Tabs */}
      <Tabs defaultValue="trends" className="space-y-4">
        <TabsList>
          <TabsTrigger value="trends">Processing Trends</TabsTrigger>
          <TabsTrigger value="suppliers">Supplier Analysis</TabsTrigger>
          <TabsTrigger value="performance">AI Performance</TabsTrigger>
          <TabsTrigger value="insights">Smart Insights</TabsTrigger>
        </TabsList>

        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Processing Volume Trends</CardTitle>
              <CardDescription>
                Monthly analysis of bill processing activity and revenue
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={analyticsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Area
                      type="monotone"
                      dataKey="processed"
                      stackId="1"
                      stroke={chartConfig.processed.color}
                      fill={chartConfig.processed.color}
                      fillOpacity={0.6}
                    />
                    <Area
                      type="monotone"
                      dataKey="errors"
                      stackId="1"
                      stroke={chartConfig.errors.color}
                      fill={chartConfig.errors.color}
                      fillOpacity={0.6}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="suppliers" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Supplier Distribution</CardTitle>
                <CardDescription>Bills processed by utility provider</CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Pie 
                        data={supplierData} 
                        dataKey="count" 
                        cx="50%" 
                        cy="50%" 
                        outerRadius={80}
                        label={(entry) => entry.name}
                      >
                        {supplierData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Supplier Performance</CardTitle>
                <CardDescription>Processing metrics by supplier</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {supplierData.slice(0, 5).map((supplier) => (
                    <div key={supplier.name} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: supplier.color }}
                        />
                        <div>
                          <p className="font-medium">{supplier.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {supplier.count} bills
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">${supplier.amount.toLocaleString()}</p>
                        <Badge variant="secondary">
                          {(supplier.avgConfidence || 0).toFixed(0)}% accuracy
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="performance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>AI Confidence Over Time</CardTitle>
              <CardDescription>
                Machine learning model performance and accuracy trends
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ChartContainer config={chartConfig} className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analyticsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis domain={[0, 100]} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Line
                      type="monotone"
                      dataKey="confidence"
                      stroke={chartConfig.confidence.color}
                      strokeWidth={3}
                      dot={{ fill: chartConfig.confidence.color, strokeWidth: 2, r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="insights" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Processing Insights</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/20">
                  <h4 className="font-semibold text-blue-900 dark:text-blue-100">Peak Processing Times</h4>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    Most bills are processed between 9 AM - 11 AM with 15% faster processing speeds.
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/20">
                  <h4 className="font-semibold text-green-900 dark:text-green-100">Accuracy Improvement</h4>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                    AI confidence has improved by 12% over the last 3 months with enhanced training data.
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/20">
                  <h4 className="font-semibold text-amber-900 dark:text-amber-100">Common Issues</h4>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    Low-quality scans account for 23% of processing errors. Consider image quality guidelines.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Optimization Recommendations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Badge variant="outline" className="mt-1">1</Badge>
                  <div>
                    <p className="font-medium">Batch Processing</p>
                    <p className="text-sm text-muted-foreground">
                      Schedule bulk uploads during off-peak hours for 20% faster processing.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Badge variant="outline" className="mt-1">2</Badge>
                  <div>
                    <p className="font-medium">File Quality</p>
                    <p className="text-sm text-muted-foreground">
                      Implement pre-upload image quality checks to reduce processing errors by 30%.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Badge variant="outline" className="mt-1">3</Badge>
                  <div>
                    <p className="font-medium">Template Training</p>
                    <p className="text-sm text-muted-foreground">
                      Create supplier-specific templates for frequently processed utility companies.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};